import { useMatomo } from '@datapunt/matomo-tracker-react';
import cx from 'clsx';
import React, { memo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as ArrowIcon } from '@assets/svg/lightArrowDown.svg';

import Button from '@components/uikit/Button/Button';

import useTranslation from '@hooks/useTranslation';

import { selectAppLocationSelectValue, useAppActions } from '@store/useAppStore';
import { useAppPersistActions } from '@store/useAppStorePersist';

import { selectMapData, selectMapGeoState, selectMapOpenTab, useMapActions } from '../../store/useMapStore';
import MapMarkerSvg from '../MapMarkerSvg/MapMarkerSvg';
import style from './style.module.scss';

const MapTabs = () => {
  const { locationId } = useParams();
  const { trackEvent } = useMatomo();
  const { t } = useTranslation();
  const { setAppPersistMapsVisible } = useAppPersistActions();
  const { setMapGeoState, setMapGeoStateInitial, setMapOpenTab } = useMapActions();
  const { setAppLocationSelectValue } = useAppActions();

  const { tabsSortedArr, continents } = selectMapData();
  const { geoUnit } = selectMapGeoState();
  const locationSelectValue = selectAppLocationSelectValue();
  const openTab = selectMapOpenTab();

  useEffect(() => {
    if (geoUnit.type === 'country') {
      setMapOpenTab(geoUnit.name);
    }

    if (locationSelectValue.id === 'all' && !locationId && tabsSortedArr.length) {
      const isOneCountry = tabsSortedArr.length === 1;

      setMapOpenTab('');

      if (isOneCountry) {
        const countryName = tabsSortedArr[0][0];

        setMapOpenTab(countryName);
        setMapGeoState({ type: 'country', name: countryName });
        return;
      }

      const continentsArr = Object.keys(continents || {});

      if (continentsArr.length === 1) {
        setMapGeoState({ type: 'continent', name: continentsArr[0] });
        return;
      }

      setMapGeoStateInitial();
    }
  }, [locationId, tabsSortedArr.length]);

  const handleTabClick = (key) => {
    trackEvent({ category: 'Map Usage', action: 'map-interaction ' });
    setMapOpenTab(openTab == key ? '' : key);
    setMapGeoState({ type: 'country', name: key });
  };

  const handleLinkClick = (region, country, name, facility_info) => {
    trackEvent({ category: 'Map Usage', action: 'map-interaction ' });
    setMapGeoState({ type: 'country', name: country, facilityName: name });
    setAppPersistMapsVisible(false);
    setAppLocationSelectValue(facility_info);
  };

  const handleGlobalMapClick = () => {
    trackEvent({ category: 'Map Usage', action: 'map-interaction ' });
    setMapGeoStateInitial();
  };

  return (
    <div className={cx(style.tabsContainer, { [style.tabsContainerPosition]: !openTab })}>
      <Button
        text={t('Global Map')}
        type="secondary"
        onClick={handleGlobalMapClick}
        className={style.globalMapButton}
      />
      <div className={style.tabsScrollbars}>
        {tabsSortedArr.map(([key, value]) => (
          <div key={key} className={style.tabContainer}>
            <div>
              <ArrowIcon
                className={cx(style.arrow, { [style.arrowActive]: openTab == key })}
                onClick={() => setMapOpenTab(openTab == key ? '' : key)}
              />
              <span
                className={cx(style.tab, { [style.tabActive]: key == geoUnit.name })}
                onClick={() => handleTabClick(key)}
              >
                {t(key)}
              </span>
            </div>
            <ul className={style.facilityItemsContainer}>
              {openTab == key &&
                value.map(({ facility_id, facility_info, region, country, name, mapMarker }) => (
                  <li key={facility_id} className={style.facilityItemContainer}>
                    <div
                      className={style.facilityItem}
                      data-tooltip-id="map-tabs-tooltip"
                      data-tooltip-content={name}
                      onClick={() => handleLinkClick(region, country, name, facility_info)}
                    >
                      {name}
                    </div>
                    <MapMarkerSvg
                      variant={mapMarker?.icon}
                      color={mapMarker?.color}
                      isVisible={name == geoUnit.facilityName}
                    />
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </div>
      <Tooltip
        id="map-tabs-tooltip"
        render={({ content, activeAnchor }) =>
          activeAnchor?.clientWidth < activeAnchor?.scrollWidth ? content : null
        }
        className={style.mapTabsTooltip}
      />
    </div>
  );
};

export default memo(MapTabs);
